import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scales, Warning, Check } from '@phosphor-icons/react';

export const WAIVER_KEY = 'unmanip_waiver_v1';
export const WAIVER_VERSION = 'v1';

export function isWaiverAccepted() {
  try {
    const raw = localStorage.getItem(WAIVER_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    return p?.version === WAIVER_VERSION && !!p?.accepted_at;
  } catch { return false; }
}

export function acceptWaiver() {
  localStorage.setItem(WAIVER_KEY, JSON.stringify({
    version: WAIVER_VERSION,
    accepted_at: new Date().toISOString(),
  }));
}

export default function WaiverGate({ children }) {
  const [ok, setOk] = useState(() => isWaiverAccepted());
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    const on = () => setOk(isWaiverAccepted());
    window.addEventListener('storage', on);
    return () => window.removeEventListener('storage', on);
  }, []);

  if (ok) return children;

  const accept = () => {
    acceptWaiver();
    setOk(true);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/95 backdrop-blur-md overflow-y-auto" data-testid="waiver-gate">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="max-w-2xl w-full panel p-6 md:p-8 border border-emerald-500/40 bg-zinc-950">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 border border-emerald-500 flex items-center justify-center">
              <Scales weight="fill" className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="label-mini text-emerald-400">A quick hello · {WAIVER_VERSION}</div>
              <h2 className="font-sans text-2xl font-black tracking-tighter text-zinc-100">Before we get started.</h2>
            </div>
          </div>

          <p className="text-sm text-zinc-300 leading-relaxed mb-4">
            We want you to feel calm using this. Here's the honest, plain-language version
            of what we are (and what we aren't) — the fine print is on the <Link to="/legal" target="_blank" className="text-emerald-400 underline">Terms page</Link> when you're ready.
          </p>

          <div className="border border-red-500/40 bg-red-500/5 p-3 mb-4 flex items-start gap-2">
            <Warning weight="fill" className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-100 leading-relaxed">
              If something is happening <b>right now</b>, please call <b>911</b>. If you're
              having thoughts of hurting yourself, call or text <b>988</b>. We're a companion, not a first responder.
            </p>
          </div>

          <ul className="text-sm text-zinc-300 space-y-2 leading-relaxed max-h-[42vh] overflow-y-auto pr-2">
            <Item>We're a <b>thoughtful second opinion</b>, not a therapist, doctor, lawyer, or investigator. Think of us as a wise friend on your shoulder.</Item>
            <Item>Our read is a <b>useful opinion — not a verdict</b>. AI can miss things and get things wrong. Please trust your own gut too.</Item>
            <Item>Photo body-language reads are <b>gentle observations</b>, not medical diagnoses. Money notes aren't financial advice. Couples reads aren't proof of anything — they're a mirror.</Item>
            <Item>Family Guardian is only for watching over <b>your own kids</b> (with your parental consent) or things people share with you. Please don't use it to read anyone else's messages.</Item>
            <Item>By continuing, you agree not to hold us or our providers responsible for anything that happens because of what our reads say — including missed alerts, delivery hiccups, or an AI that got it wrong. Our liability is capped at <b>$10</b> or what you paid us in the past year, whichever is bigger. That's the honest deal so we can afford to keep this free/cheap.</Item>
            <Item>Alerts, SMS, and pushes travel over the internet and cell networks — <b>we can't guarantee they'll arrive on time</b>. Please still call 911 if it's urgent.</Item>
            <Item>Please don't use us to hurt, stalk, or manipulate anyone. We watch for that pattern and quietly shut it down.</Item>
            <Item>You're <b>18+</b> (or the parent of the child being protected).</Item>
            <Item><b>Nothing you share is stored.</b> We can't hand over your history to anyone — even us. Payment records are kept because the law asks us to.</Item>
            <Item>Unlocks are non-refundable once used. Disagreements go to friendly arbitration (Delaware). You waive class-action rights so we can keep this cheap for everyone.</Item>
          </ul>

          <p className="mt-4 text-xs text-zinc-500">
            Full plain-english + legal text: <Link to="/legal" target="_blank" className="text-emerald-400 underline">Terms &amp; Waiver</Link> · legal@unmanipulated.app
          </p>

          <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="waiver-checkbox-label">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="waiver-checkbox"
              className="mt-1 h-4 w-4 accent-emerald-500"
            />
            <span className="text-sm text-zinc-200 leading-relaxed">
              I have read and I <b>accept</b> the Terms &amp; Liability Waiver. I understand this
              is an AI opinion tool, not a professional service, and I <b>waive and release</b>
              all claims against the Released Parties as described.
            </span>
          </label>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={accept}
              disabled={!checked}
              data-testid="waiver-accept-btn"
              className="inline-flex items-center gap-2 border border-emerald-500 bg-emerald-500 px-6 py-3 text-xs uppercase tracking-[0.24em] font-bold text-zinc-950 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check weight="bold" className="h-4 w-4" /> I understand &amp; accept
            </button>
            <a
              href="https://www.google.com"
              className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-zinc-300"
            >
              decline &amp; leave
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Item({ children }) {
  return (
    <li className="border-l-2 border-emerald-500/50 pl-3 py-0.5">{children}</li>
  );
}
